import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { OtaField } from "./helpers";
import DeleteBookingButton from "./DeleteBookingButton";

export default function OtaBookingDetailModal({ item, onClose, otaProviderLabel, IT, onSaved, rooms }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingPs, setSavingPs] = useState(false);
  useEffect(() => {
    if (!item) return;
    // Prefer the manual override (host-set) so the input echoes what will
    // actually be persisted; fall back to the flat effective value the API
    // exposes (overrides > extracted). Store as a string so the input stays
    // controlled even when the value is null.
    const guestsInit =
      (item.manual_overrides && item.manual_overrides.guests != null)
        ? String(item.manual_overrides.guests)
        : (item.guests != null ? String(item.guests) : "");
    setForm({
      guest_name: item.guest_name || "",
      guest_email: item.guest_email || "",
      guest_phone: item.guest_phone || "",
      check_in: (item.check_in || "").slice(0, 10),
      check_out: (item.check_out || "").slice(0, 10),
      room_id: item.room_id || "",
      amount: item.amount != null ? String(item.amount) : "",
      guests: guestsInit,
      notes:
        (item.manual_overrides && item.manual_overrides.notes)
        || item.notes
        || "",
      payment_status: item.payment_status || "pending",
    });
  }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const meta = otaProviderLabel(item.source);
  const nights = (() => {
    try {
      const ms = new Date(form.check_out) - new Date(form.check_in);
      return Math.max(1, Math.round(ms / 86400000));
    } catch { return null; }
  })();
  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const changePaymentStatus = async (v) => {
    setField("payment_status", v);
    setSavingPs(true);
    try {
      await api.patch(`/bookings/${item.id}/payment-status`, { payment_status: v });
      toast.success(IT ? "Stato pagamento aggiornato" : "Payment status updated");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setSavingPs(false); }
  };
  const saveAll = async () => {
    const amountVal = form.amount.trim() === "" ? null : Number(form.amount.replace(",", "."));
    if (form.amount.trim() !== "" && (!isFinite(amountVal) || amountVal < 0)) {
      toast.error(IT ? "Importo non valido" : "Invalid amount");
      return;
    }
    // Guests: empty string → 0 on the wire (backend interprets 0 as "clear
    // the override" and reverts to the feed-parsed extracted.guests). Any
    // positive integer 1-20 is stored verbatim in manual_overrides.guests.
    let guestsVal = null;
    const gTrim = (form.guests || "").toString().trim();
    if (gTrim !== "") {
      const g = parseInt(gTrim, 10);
      if (!Number.isFinite(g) || g < 0 || g > 20) {
        toast.error(IT ? "Numero ospiti non valido (0–20)" : "Invalid guests (0–20)");
        return;
      }
      guestsVal = g;
    }
    setSaving(true);
    try {
      await api.put(`/ota-bookings/${item.id}`, {
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        guest_phone: form.guest_phone,
        check_in: form.check_in,
        check_out: form.check_out,
        room_id: form.room_id,
        amount: amountVal,
        notes: (form.notes || "").trim(),
        ...(guestsVal !== null ? { guests: guestsVal } : {}),
      });
      toast.success(IT ? "Prenotazione aggiornata" : "Booking updated");
      onSaved?.({ ...form, amount: amountVal });
      onClose?.();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setSaving(false); }
  };
  const inputCls = "w-full px-3 py-1.5 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40";
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ota-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-white font-semibold rounded px-2 py-0.5" style={{ background: meta.color }}>
              {meta.label}
            </span>
            <DialogTitle className="text-2xl font-display">{form.guest_name || "—"}</DialogTitle>
          </div>
          <DialogDescription>
            {IT ? "Correggi qualsiasi campo — le modifiche persistono anche dopo la sincronizzazione iCal." : "Edit any field — changes persist through iCal re-syncs."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1 mt-2">
          <OtaField label={IT ? "Nome ospite" : "Guest name"} testid="ota-det-name">
            <input type="text" value={form.guest_name} onChange={(e) => setField("guest_name", e.target.value)} className={inputCls} data-testid="ota-edit-name"/>
          </OtaField>
          <OtaField label="Email" testid="ota-det-email">
            <input type="email" value={form.guest_email} onChange={(e) => setField("guest_email", e.target.value)} className={inputCls} data-testid="ota-edit-email"/>
          </OtaField>
          <OtaField label={IT ? "Telefono" : "Phone"} testid="ota-det-phone">
            <input type="tel" value={form.guest_phone} onChange={(e) => setField("guest_phone", e.target.value)} className={inputCls} data-testid="ota-edit-phone"/>
          </OtaField>
          <OtaField label="Check-in" testid="ota-det-checkin">
            <input type="date" value={form.check_in} onChange={(e) => setField("check_in", e.target.value)} className={inputCls} data-testid="ota-edit-checkin"/>
          </OtaField>
          <OtaField label="Check-out" testid="ota-det-checkout">
            <input type="date" value={form.check_out} onChange={(e) => setField("check_out", e.target.value)} className={inputCls} data-testid="ota-edit-checkout"/>
          </OtaField>
          <div className="grid grid-cols-3 gap-3 py-2 border-b border-border/40" data-testid="ota-inline-triple">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">{IT ? "Camera" : "Room"}</div>
              <select value={form.room_id} onChange={(e) => setField("room_id", e.target.value)} className={inputCls} data-testid="ota-edit-room">
                <option value="">{IT ? "— Nessuna —" : "— None —"}</option>
                {(rooms || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                {IT ? "Numero ospiti" : "Guests"}
                {(item.manual_overrides?.guests) && (
                  <span
                    className="shrink-0 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-sage/15 text-sage border border-sage/40"
                    title={IT ? "Numero impostato manualmente dall'host — non verrà sovrascritto dai re-sync iCal" : "Manually set by the host — iCal re-sync will not overwrite it"}
                    data-testid="ota-guests-manual-badge"
                  >
                    <Pencil className="w-2.5 h-2.5"/>{IT ? "Manuale" : "Manual"}
                  </span>
                )}
              </div>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={20}
                step={1}
                value={form.guests}
                onChange={(e) => setField("guests", e.target.value)}
                placeholder={IT ? "es. 2" : "e.g. 2"}
                className={inputCls}
                data-testid="ota-guests-input"
                aria-label={IT ? "Numero occupanti" : "Number of guests"}
              />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">{IT ? "Notti" : "Nights"}</div>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={365}
                step={1}
                value={nights || ""}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!Number.isFinite(n) || n < 1 || n > 365) return;
                  if (!form.check_in) return;
                  const ci = new Date(form.check_in + "T00:00:00");
                  if (Number.isNaN(ci.getTime())) return;
                  const co = new Date(ci.getTime() + n * 86400000);
                  const iso = `${co.getFullYear()}-${String(co.getMonth() + 1).padStart(2, "0")}-${String(co.getDate()).padStart(2, "0")}`;
                  setField("check_out", iso);
                }}
                className={`${inputCls} font-mono text-center`}
                data-testid="ota-nights-input"
                aria-label={IT ? "Numero notti" : "Number of nights"}
              />
            </div>
          </div>
          <OtaField label={IT ? "Importo (€)" : "Amount (€)"} testid="ota-det-amount">
            <input type="text" inputMode="decimal" value={form.amount} onChange={(e) => setField("amount", e.target.value)} placeholder={IT ? "es. 280.00" : "e.g. 280.00"} className={inputCls} data-testid="ota-amount-input"/>
          </OtaField>
          {(item.city_tax_amount != null && Number(item.city_tax_amount) > 0) && (
            <div
              className="grid grid-cols-[140px_1fr] gap-3 py-2 border-b border-border/40 items-start"
              data-testid="ota-city-tax"
              title={IT ? "Contabilizzata separatamente — non concorre a commissioni HH né IVA sull'alloggio." : "Booked separately — excluded from HH commission and lodging VAT."}
            >
              <div className="text-[11px] uppercase tracking-widest text-primary font-semibold pt-0.5">
                {IT ? "Imposta soggiorno" : "City tax"}
              </div>
              <div className="text-sm">
                <span className="font-mono font-semibold text-primary" data-testid="ota-city-tax-amount">€ {Number(item.city_tax_amount).toFixed(2)}</span>
                {item.city_tax_breakdown && (
                  <span className="text-[10px] text-muted-foreground ml-2">
                    {IT
                      ? `${item.city_tax_breakdown.taxable_guests} ospiti × ${item.city_tax_breakdown.nights_charged} notti × € ${Number(item.city_tax_breakdown.amount_per_person_per_night).toFixed(2)}${item.city_tax_breakdown.exempt_guests ? ` · ${item.city_tax_breakdown.exempt_guests} esente/i` : ""}`
                      : `${item.city_tax_breakdown.taxable_guests} guests × ${item.city_tax_breakdown.nights_charged} nights × € ${Number(item.city_tax_breakdown.amount_per_person_per_night).toFixed(2)}${item.city_tax_breakdown.exempt_guests ? ` · ${item.city_tax_breakdown.exempt_guests} exempt` : ""}`}
                  </span>
                )}
                <p className="text-[10px] text-muted-foreground italic mt-0.5">
                  {IT ? "Contabilità separata da fatturato e commissioni." : "Tracked separately from revenue and commission."}
                </p>
              </div>
            </div>
          )}
          <OtaField label={IT ? "Rif. esterno" : "External ref"} testid="ota-det-extref">
            <span className="text-xs font-mono text-muted-foreground break-all">{item.external_ref || "—"}</span>
          </OtaField>
          <OtaField label={IT ? "Note" : "Notes"} testid="ota-det-notes">
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder={IT
                ? "Note interne per l'host (allergie, VIP, orario di arrivo particolare, …). Lascia vuoto per rimuovere."
                : "Internal host notes (allergies, VIP, unusual ETA, …). Empty to remove."}
              className="text-sm resize-none"
              data-testid="ota-notes-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              {IT
                ? "Salvato in modo permanente — non verrà sovrascritto dai re-sync iCal."
                : "Stored permanently — iCal re-sync will not overwrite it."}
            </p>
          </OtaField>
          <OtaField label={IT ? "Stato pagamento" : "Payment status"} testid="ota-det-payment-status">
            <div className="flex items-center gap-2">
              <select
                value={form.payment_status}
                onChange={(e) => changePaymentStatus(e.target.value)}
                disabled={savingPs}
                className={inputCls}
                data-testid="ota-payment-status-select"
              >
                <option value="pending">{IT ? "Pendente" : "Pending"}</option>
                <option value="paid">{IT ? "Pagato" : "Paid"}</option>
                <option value="refunded">{IT ? "Rimborsato" : "Refunded"}</option>
                <option value="no_show_charged">{IT ? "Penale incassata" : "Penalty charged"}</option>
              </select>
              {(item.manual_overrides?.payment_status) && (
                <span
                  className="shrink-0 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-sage/15 text-sage border border-sage/40"
                  title={IT ? "Stato impostato manualmente dall'host — non verrà sovrascritto dai re-sync iCal" : "Manually set by the host — iCal re-sync will not overwrite it"}
                  data-testid="ota-payment-manual-badge"
                >
                  <Pencil className="w-2.5 h-2.5"/>{IT ? "Manuale" : "Manual"}
                </span>
              )}
            </div>
          </OtaField>
        </div>
        <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-border">
          <DeleteBookingButton booking={item} onDeleted={() => { onSaved?.(); onClose?.(); }} IT={IT}/>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:border-destructive hover:text-destructive transition-all">
              {IT ? "Annulla" : "Cancel"}
            </button>
            <button type="button" onClick={saveAll} disabled={saving} data-testid="ota-amount-save-btn" className="text-xs px-4 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-all disabled:opacity-50 font-semibold">
              {saving ? "…" : (IT ? "Salva modifiche" : "Save changes")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
